import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { invalidateClientRow } from "@/lib/clientRowCache";
import {
  backupSession,
  markManualSignOut,
  primeSessionBackup,
  recoverSession,
  wasManualSignOut,
  getSessionBackup,
} from "@/lib/sessionPersistence";

export type AppRole = "admin" | "coach" | "client";

const AUTH_TIMEOUT = "AUTH_TIMEOUT";

function withTimeout<T>(promise: PromiseLike<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = window.setTimeout(() => reject(new Error(`${AUTH_TIMEOUT}:${label}`)), ms);
    Promise.resolve(promise).then(
      (value) => {
        window.clearTimeout(id);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(id);
        reject(error);
      },
    );
  });
}

type AuthState = {
  session: Session | null;
  user: User | null;
  /** Highest-priority role (admin > coach > client). Drives default routing. */
  role: AppRole | null;
  /**
   * EVERY role granted to this single account. A coach who also trains as an
   * athlete is one identity with both roles — screens should read this instead
   * of assuming `role` is the whole story.
   */
  roles: AppRole[];
  /** Admin privileges (badge, admin panel). Never inferred from `coach`. */
  isAdmin: boolean;
  /** Has a coaching side (coach dashboard). Admins are coaches too. */
  isCoach: boolean;
  /** Has an athlete side of the same account. */
  isAthlete: boolean;
  /** True only while the very first session+role bootstrap is in flight. */
  loading: boolean;
  /** True while a post-login role refresh is in flight. */
  refreshing: boolean;
  /** Set when the role lookup failed (network, RLS, etc.). null on success. */
  roleError: string | null;
  signOut: () => Promise<void>;
  refreshRole: () => Promise<void>;
};

export const AuthContext = createContext<AuthState | undefined>(undefined);

type RoleResult =
  | { ok: true; role: AppRole | null; roles: AppRole[] }
  | { ok: false; error: string };

/**
 * Short-lived cache of the role lookup. Sign-in fires several auth events in
 * quick succession (initial session, token refresh, visibility), and each one
 * used to trigger its own `user_roles` query on app start.
 */
let roleCache: { userId: string; at: number; promise: Promise<RoleResult> } | null = null;
const ROLE_TTL_MS = 30_000;

export function clearRoleCache(): void {
  roleCache = null;
}

async function fetchPrimaryRole(
  userId: string,
  label: string = "role",
  force = false,
): Promise<RoleResult> {
  if (!force && roleCache && roleCache.userId === userId && Date.now() - roleCache.at < ROLE_TTL_MS) {
    return roleCache.promise;
  }
  const promise = fetchPrimaryRoleUncached(userId, label);
  roleCache = { userId, at: Date.now(), promise };
  const res = await promise;
  // Don't cache failures — the next caller should retry.
  if (res.ok === false && roleCache?.promise === promise) roleCache = null;
  return res;
}

async function fetchPrimaryRoleUncached(userId: string, label: string): Promise<RoleResult> {
  const t0 = performance.now();
  try {
    const { data, error } = await withTimeout(
      supabase.from("user_roles").select("role").eq("user_id", userId),
      7000,
      `role:${label}`,
    );
    const t1 = performance.now();
    console.log(`[timing] fetchPrimaryRole (${label}) took ${(t1 - t0).toFixed(1)}ms`);
    if (error) return { ok: false, error: error.message };
    // A signed-in account with no explicit row is treated as a client so auth
    // redirects can complete instead of looping back to /auth forever. Data
    // access is still protected by backend policies.
    if (!data || data.length === 0) return { ok: true, role: "client", roles: ["client"] };
    const roles = data.map((r) => r.role as AppRole);
    const primary: AppRole = roles.includes("admin")
      ? "admin"
      : roles.includes("coach")
        ? "coach"
        : "client";
    return { ok: true, role: primary, roles };
  } catch (error) {
    const t1 = performance.now();
    console.warn(`[auth] fetchPrimaryRole (${label}) failed after ${(t1 - t0).toFixed(1)}ms`, error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : String(error ?? "Role lookup failed"),
    };
  }
}

/**
 * True when a supabase auth token is present in local storage, i.e. the user
 * logged in previously and the session should be restored rather than dropped.
 */
function hasPersistedSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && /^sb-.*-auth-token$/.test(key) && localStorage.getItem(key)) return true;
    }
  } catch {
    /* storage unavailable */
  }
  return false;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);
  // Always-current session, readable from listeners that outlive a render.
  const latestSession = useRef<Session | null>(null);

  const applyRoleResult = useCallback((res: RoleResult) => {
    if (res.ok === true) {
      setRole(res.role);
      setRoles(res.roles);
      setRoleError(null);
      return;
    }
    setRole(null);
    setRoles([]);
    setRoleError(res.error);
  }, []);

  // Warm up the ai-call edge function once the user is signed in. We send a
  // single OPTIONS preflight (no body, no quota usage) which forces Cloud
  // to spin up the function ahead of the first real AI request — making
  // the first generated meal/workout/posing call feel ~1-3 s faster.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-call`;
      fetch(url, {
        method: "OPTIONS",
        headers: {
          "Access-Control-Request-Method": "POST",
          "Access-Control-Request-Headers": "authorization, content-type",
        },
        // Keep this completely best-effort; never block UI or surface errors.
        keepalive: true,
      }).catch(() => {});
    };
    // Defer one tick so we don't compete with the role lookup / first paint.
    const id = window.setTimeout(warm, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [user?.id]);


  // CRITICAL ORDER: subscribe BEFORE calling getSession to avoid missing events.
  useEffect(() => {
    let cancelled = false;

    /**
     * Keep attempting recovery while we still hold a refresh token that the
     * server has not rejected. Only a manual sign-out or a dead token ends this.
     */
    let recovering = false;
    const keepRecovering = async (): Promise<boolean> => {
      if (recovering) return false;
      recovering = true;
      try {
        for (let round = 0; round < 3; round++) {
          if (cancelled || wasManualSignOut()) return false;
          if (!getSessionBackup()) return false; // token definitively dead
          if (await recoverSession(supabase)) return true;
          if (!getSessionBackup()) return false;
          await new Promise((r) => setTimeout(r, 4_000 * (round + 1)));
        }
        return false;
      } finally {
        recovering = false;
      }
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      latestSession.current = newSession;

      // Keep our own durable copy of the refresh token on every event that
      // carries a session, so a spurious sign-out can be repaired.
      if (newSession) backupSession(newSession);

      // A silent token refresh must never look like a sign-out or trigger a
      // fresh role lookup — the user is the same, only the JWT changed.
      if (_event === "TOKEN_REFRESHED") {
        setLoading(false);
        return;
      }

      if (newSession?.user) {
        setRoleError(null);
        // Defer role lookup so we never run a Supabase call inside the auth callback.
        setTimeout(() => {
          fetchPrimaryRole(newSession.user.id, `onAuthStateChange:${_event}`)
            .then(applyRoleResult)
            .catch((e) => applyRoleResult({ ok: false, error: String(e) }))
            .finally(() => setLoading(false));
        }, 0);
        return;
      }

      // Only an explicit sign-out (or a user deletion) clears local auth state.
      // Other event types with a null session (e.g. INITIAL_SESSION before the
      // persisted session is read back from storage) must not log the user out.
      if (_event === "SIGNED_OUT" || _event === "USER_UPDATED") {
        // A SIGNED_OUT that the user did not ask for means supabase-js dropped
        // the session (typically a token refresh that raced with another tab or
        // an app wake-up). Silently restore it from our backup instead of
        // dumping the user on the login screen, and keep retrying for as long as
        // we still hold a refresh token the server hasn't rejected.
        if (_event === "SIGNED_OUT" && !wasManualSignOut() && getSessionBackup()) {
          setTimeout(() => void keepRecovering(), 0);
          return;
        }
        setRole(null);
        setRoleError(null);
        setLoading(false);
      }
    });


    // Restore the persisted session. Brokered/async storage can be slow on a
    // cold start, so retry instead of falling back to a signed-out state.
    const restore = async (attempt = 1): Promise<void> => {
      try {
        if (attempt === 1) await primeSessionBackup();
        const { data: { session: existing } } = await withTimeout(
          supabase.auth.getSession(),
          8000,
          "getSession",
        );
        if (cancelled) return;
        if (existing?.user) {
          setSession(existing);
          setUser(existing.user);
          latestSession.current = existing;
          backupSession(existing);
          const res = await fetchPrimaryRole(existing.user.id, "getSession");
          if (cancelled) return;
          applyRoleResult(res);
          setLoading(false);
          return;
        }
        if (attempt < 2 && hasPersistedSession()) {
          // Storage says there is a session but supabase-js hasn't hydrated it
          // yet — give it one more chance before rendering as logged out.
          await new Promise((r) => setTimeout(r, 400));
          return restore(attempt + 1);
        }
        // No live session but we still hold a refresh token from a previous
        // sign-in that was never manually ended — restore it. This is what makes
        // login survive app closes, restarts and long suspends.
        if (!wasManualSignOut() && getSessionBackup()) {
          const ok = await recoverSession(supabase);
          if (cancelled) return;
          if (ok) return; // onAuthStateChange takes it from here
          // Couldn't reach the server (offline app open). Don't hold the spinner
          // forever, but keep trying in the background so the session comes back
          // by itself as soon as there is a connection.
          if (getSessionBackup()) void keepRecovering();
        }
        setSession(null);
        setUser(null);
        setLoading(false);
      } catch (e) {
        console.warn("[auth] getSession failed", e);
        if (cancelled) return;
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, 500 * attempt));
          return restore(attempt + 1);
        }
        setLoading(false);
      }
    };
    void restore();

    // Timers are throttled while the app is backgrounded, so the built-in
    // auto-refresh can be late after a long suspend. Nudge it back on — but do
    // NOT fire an extra concurrent refresh: that is what used to burn the
    // rotated refresh token and log people out on reopen.
    let revalidateAt = 0;
    const revalidate = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - revalidateAt < 5_000) return;
      revalidateAt = Date.now();
      supabase.auth.startAutoRefresh?.().catch(() => {});
      if (!wasManualSignOut()) {
        void primeSessionBackup().then((hasBackup) => {
          if (!hasBackup) return;
          return supabase.auth.getSession().then(({ data }) => {
            if (data.session) backupSession(data.session);
            else void keepRecovering();
          });
        }).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("online", revalidate);
    // Being put in the background / restored from the page cache are the exact
    // moments a mobile browser tears the tab down, so refresh the durable copy
    // then too and re-check on the way back in.
    const onPageHide = () => {
      const current = latestSession.current;
      if (current) backupSession(current);
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", revalidate);
    window.addEventListener("focus", revalidate);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("online", revalidate);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", revalidate);
      window.removeEventListener("focus", revalidate);
      sub.subscription.unsubscribe();
    };
  }, [applyRoleResult]);

  const refreshRole = async () => {
    if (!user) return;
    setRefreshing(true);
    const res = await fetchPrimaryRole(user.id, "refreshRole", true);
    applyRoleResult(res);
    setRefreshing(false);
  };

  const signOut = async () => {
    clearRoleCache();
    invalidateClientRow();
    // Tapping sign out is the only thing that may end the session for good.
    markManualSignOut();
    await supabase.auth.signOut();
    setRole(null);
    setRoles([]);
    setRoleError(null);
  };

  // One account, possibly several hats. `role` still drives default routing;
  // these flags let screens show both sides of the same identity.
  const isAdmin = roles.includes("admin") || role === "admin";
  const isCoach = isAdmin || roles.includes("coach") || role === "coach";
  const isAthlete = roles.includes("client") || role === "client" || isCoach;

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        role,
        roles,
        isAdmin,
        isCoach,
        isAthlete,
        loading,
        refreshing,
        roleError,
        signOut,
        refreshRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
